import { useCallback, useEffect, useMemo, useState } from 'react'
import { useProjectPlanSnapshotRepository } from '../../../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { TeamSelectRow } from '../model'

type DataSourceType = 'snapshot' | 'template'

interface UseTeamSelectDataProps {
  type: DataSourceType
  uuid: string
  selectedUuids: string[]
}

const teamDataCache = new Map<string, Omit<TeamSelectRow, 'selected'>[]>()

export const clearTeamDataCache = () => {
  teamDataCache.clear()
}

export const useTeamSelectData = ({
  type,
  uuid,
  selectedUuids,
}: UseTeamSelectDataProps) => {
  const { fetchReferencedValues, fetchAllTeamByProject } =
    useProjectPlanSnapshotRepository()
  const [partialRows, setPartialRows] = useState<
    Omit<TeamSelectRow, 'selected'>[]
  >([])

  const fetchSnapshotData = useCallback(
    async (snapshotUuid: string) => {
      const response = await fetchReferencedValues(snapshotUuid, 'TEAM_ENTITY')
      return response.map(resp => ({
        uuid: resp.value,
        displayName: resp.name,
      }))
    },
    [fetchReferencedValues]
  )

  const fetchTemplateData = useCallback(
    async (projectUuid: string): Promise<Omit<TeamSelectRow, 'selected'>[]> => {
      try {
        if (teamDataCache.has(projectUuid)) {
          return teamDataCache.get(projectUuid)!
        }

        const response = await fetchAllTeamByProject(projectUuid)

        if (!response.json) {
          const emptyResult: Omit<TeamSelectRow, 'selected'>[] = []
          teamDataCache.set(projectUuid, emptyResult)
          return emptyResult
        }

        const teams = Array.isArray(response.json)
          ? response.json
          : [response.json]
        const teamRows = teams.map(team => ({
          uuid: team.uuid,
          displayName: team.displayName,
        }))

        teamDataCache.set(projectUuid, teamRows)
        return teamRows
      } catch (error) {
        console.error('Error in fetchTemplateData:', error)
        const emptyResult: Omit<TeamSelectRow, 'selected'>[] = []
        teamDataCache.set(projectUuid, emptyResult)
        return emptyResult
      }
    },
    [fetchAllTeamByProject]
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result: Omit<TeamSelectRow, 'selected'>[]

        if (type === 'snapshot') {
          result = await fetchSnapshotData(uuid)
        } else {
          result = await fetchTemplateData(uuid)
        }

        setPartialRows(result)
      } catch (error) {
        console.error('Failed to fetch team data:', error)
        setPartialRows([])
      }
    }

    fetchData()
  }, [type, uuid, fetchSnapshotData, fetchTemplateData])
  const rows = useMemo(
    () =>
      partialRows.map(r => {
        const selected = selectedUuids.includes(r.uuid)
        return {
          ...r,
          selected,
        } as TeamSelectRow
      }),
    [partialRows, selectedUuids]
  )
  return {
    rows,
  }
}

export const useTeamSelectSnapshotData = (
  snapshotUuid: string,
  selectedUuids: string[]
) => {
  return useTeamSelectData({
    type: 'snapshot',
    uuid: snapshotUuid,
    selectedUuids,
  })
}

export const useTeamSelectTemplateData = (
  projectUuid: string,
  selectedUuids: string[]
) => {
  return useTeamSelectData({
    type: 'template',
    uuid: projectUuid,
    selectedUuids,
  })
}
